import { Controller } from "@hotwired/stimulus"

const FIRST_CHECK_MS = 1000
const BACKOFF = 1.5
const MAX_INTERVAL_MS = 10000
const GIVE_UP_AFTER_MS = 2 * 60 * 1000

// The second way a background search's results reach the page. The job's
// "ready" broadcast normally replaces this placeholder within a second of the
// search finishing, but that broadcast depends on the web process's solid_cable
// listener thread, and when the thread dies nothing else notices. So this asks
// the results endpoint with HEAD -- 202 means the search is still running --
// and on any other answer swaps in the same lazy frame the broadcast would have
// delivered, which then fetches the results, the search's error, or the
// "expired" notice. Whichever path lands first replaces this element, and its
// disconnect stops the polling.
export default class extends Controller {
  static targets = ["frame", "unavailable"]
  static values = { url: String }

  connect() {
    this.interval = FIRST_CHECK_MS
    this.failingSince = null
    this.schedule()
  }

  disconnect() {
    clearTimeout(this.timer)
    this.timer = null
  }

  schedule() {
    this.timer = setTimeout(() => this.check(), this.interval)
    this.interval = Math.min(this.interval * BACKOFF, MAX_INTERVAL_MS)
  }

  async check() {
    let response
    try {
      response = await fetch(this.urlValue, {
        method: "HEAD",
        credentials: "same-origin",
        headers: { Accept: "text/html" }
      })
    } catch {
      // A network blip is not an answer -- it counts toward giving up below.
    }
    if (!this.timer) return // replaced while the request was in flight

    if (response && (response.redirected || response.status === 401)) {
      // Signed out mid-search: the poll followed Devise to the sign-in page.
      // Loading that into the frame would read "Content missing", so send the
      // whole page there instead -- sign-in returns to this search afterwards.
      window.location.reload()
    } else if (response && response.status === 202) {
      this.failingSince = null
      this.schedule()
    } else if (!response || response.status >= 500) {
      this.failingSince ??= Date.now()
      if (Date.now() - this.failingSince < GIVE_UP_AFTER_MS) {
        this.schedule()
      } else {
        this.swapIn(this.unavailableTarget)
      }
    } else {
      this.swapIn(this.frameTarget)
    }
  }

  swapIn(template) {
    this.element.replaceWith(template.content.cloneNode(true))
  }
}
